import { clientActions, useAuthenticated, useMarketDataWS } from '@cmctechnology/webinvest-store-client';
import { frontendActions, OPEN_SELF_URL_TARGET, useFrontendContext } from '@cmctechnology/webinvest-store-frontend';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';
import { IStore } from '../store/store';
import { AppStage, THEMES, Theme, getThemeConfig } from '@cmctechnology/webinvest-constants';
import { Dispatch, useEffect, useState } from 'react';
import { marketdataActions } from '@cmctechnology/webinvest-store-marketdata';
import { WebTradingPreLoader } from './WebTradingPreLoader';
import { SalesforceChatAgent } from '../Modules';
import { SESSION_EXPIRED, AU_LOGIN_PATH, SPLASH_SCREEN_PROPS } from '../constants/constants';
import { StockLendingInterceptorFrame } from './StockLendingInterceptorFrame';
import { SplashScreenZoomDisabledWrapper } from './SplashScreenZoomDisabledWrapper';
import { styled } from 'styled-components';

const PixelIframe = styled.iframe`
  position: absolute;
  width: 0;
  height: 0;
  border: 0;
`;

const signOutAndRedirect = async (dispatch: Dispatch<any>, theme: Theme) => {
  const url = getThemeConfig(theme, process.env.REACT_APP_STAGE as AppStage).stockbrokingPlatformUrl;
  await dispatch(clientActions.signOut());
  frontendActions.redirectToUrl(url, OPEN_SELF_URL_TARGET);
};

export const Authenticated: React.FC = () => {
  const { finished, authenticated } = useAuthenticated();
  const settings = useSelector((store: IStore) => store.client.settings);
  const location = useLocation();
  const dispatch = useDispatch();
  // TODO: The type of 'theme' prop of IClientStore in the webinvest-store-client will be refactored to use the type Theme of webinvest-constants repo instead of web-design's
  const currentTheme = useSelector((store: IStore) => store.client.theme);
  const forcedLogout = useSelector((store: IStore) => store.client.session.forceLogout);
  const crc = useSelector((store: IStore) => store.client.crc);
  const { theme, publicUrl, setTheme } = useFrontendContext();
  const { isWsAuthenticated } = useMarketDataWS(authenticated);
  const [showOverlay, setShowOverlay] = useState(false);
  const marketingUrl = theme == THEMES.CMC ? getThemeConfig(theme, process.env.REACT_APP_STAGE as AppStage).websiteUrl : undefined;

  // TODO: Remove override after ASB/Westpac demos are done! also re-enable coverage
  useEffect(() => {
    const themeOverride = localStorage.getItem('themeOverride') as Theme;
    if (themeOverride) {
      setTheme(themeOverride as Theme);
    } else if (currentTheme && theme !== currentTheme) {
      setTheme(currentTheme as Theme);
    }
  }, [currentTheme, theme]);

  useEffect(() => {
    marketdataActions.setFxRateUseWs(isWsAuthenticated);
    marketdataActions.setQuotesUseWs(isWsAuthenticated);
  }, [isWsAuthenticated]);

  useEffect(() => {
    if (crc) dispatch<any>(clientActions.sessionCheck());
  }, [location]);

  if (!finished) return <SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />;

  if (!authenticated) {
    if (forcedLogout) {
      localStorage.setItem(SESSION_EXPIRED, `true`);
      return <Navigate to='/logout' />;
    }

    // navigating to /{region}/login, instead of assigning redirect route.
    // redirecting to /dashboard is handled in the webinvest-module-login-fg.
    // after login success, will redirect to /dashboard.
    // integrated/changed this as per SBCI-1803.
    //const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    //const redirectQuery = redirectUrl ? `?redirect_url=${redirectUrl}` : ``;
    //navigate(`${publicUrl}/sg/login${redirectQuery}`);

    const regionPath = theme ? `/${getThemeConfig(theme).themePath}/login` : AU_LOGIN_PATH;
    return <Navigate to={`${publicUrl}${regionPath}`} />;
  }

  if (!settings.FalconAccess || !settings.FalconEnabled) {
    if (!showOverlay) setShowOverlay(true);
    signOutAndRedirect(dispatch, theme);
    return <></>;
  }

  return (
    <>
      <WebTradingPreLoader />
      <SalesforceChatAgent marketing={false} />
      <StockLendingInterceptorFrame />
      <Outlet />
      {marketingUrl && <PixelIframe src={`${marketingUrl}stockbroking/client?platform=new`}></PixelIframe>}
      {showOverlay && <SplashScreenZoomDisabledWrapper {...SPLASH_SCREEN_PROPS} />}
    </>
  );
};
